import {
  useCreateMessage,
  useDoctolibReleve,
  useEmailSettings,
  useMessages,
  useSetCategoryEnabled,
  useSetMessageEnabled,
  useUpdateMessage,
  type MessageCatalogEntry,
  type MessagesResult,
  type MessageTemplate,
} from '@fidalia/api';

import { Alert, PageHeader, Spinner } from '@fidalia/ui';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useEffect, useMemo, useRef, useState } from 'react';
import { MdStickyNote2 } from 'react-icons/md';

import { AlerteExtensionRequise } from '@/features/doctolib/alerte-extension';
import { useSynchroniserDoctolib } from '@/features/doctolib/use-synchroniser';
import { ModalAvisGoogle } from '@/features/messages/avis-google-modal';
import { ModalConsignes } from '@/features/messages/consignes-modal';
import { ModalMessageActif } from '@/features/messages/message-actif-modal';
import { isReviewLink } from '@/features/reviews/google-link';
import { CartePredefini } from '@/features/messages/predefinis';
import { ModalRappel } from '@/features/messages/rappel-modal';
import { ModalSyncDoctolib } from '@/features/messages/sync-doctolib-modal';
import { ModalTest } from '@/features/messages/test-modal';
import {
  APPOINTMENT_REMINDER,
  casesOf,
  fallbackOf,
  GOOGLE_REVIEW,
  INSTRUCTION_KEYS,
  templatesOf,
} from '@/features/messages/types';
import { track } from '@/lib/analytics';

export const Route = createFileRoute('/_app/messages/')({
  component: MessagesPage,
});

/**
 * Ce que dit la confirmation, par catégorie.
 *
 * Une table plutôt qu'une suite de ternaires : elles se lisaient encore à deux
 * catégories, plus à trois.
 */
const CONFIRMATIONS: Record<string, { titre: string; description: string }> = {
  [GOOGLE_REVIEW]: {
    titre: 'Vos demandes d’avis sont programmées',
    description:
      'Chaque patient recevra le message directement après sa consultation. Vous n’avez plus rien à faire.',
  },
  [APPOINTMENT_REMINDER]: {
    titre: 'Vos rappels sont programmés',
    description:
      'Chaque patient recevra son rappel la veille du rendez-vous. Vous n’avez plus rien à faire.',
  },
  'pre-op-instructions': {
    titre: 'Vos consignes sont programmées',
    description:
      'Les patients concernés recevront leurs consignes avant le soin. Vous n’avez plus rien à faire.',
  },
  'post-op-instructions': {
    titre: 'Vos consignes sont programmées',
    description:
      'Les patients concernés recevront leurs consignes après le soin. Vous n’avez plus rien à faire.',
  },
};

/**
 * À partir de combien d'envois restants on prévient.
 *
 * Pas de compte à rebours dès le premier message : ce qui compte, c'est que la
 * fin du forfait ne tombe pas sans prévenir, pas qu'on rappelle à chaque visite
 * qu'il existe.
 */
const SEUIL_ALERTE_FORFAIT = 20;

/**
 * Ce que l'écran dit de l'envoi, et quand il le dit.
 *
 * Trois états, pas plus : silence tant que le forfait tient, un mot quand il
 * s'épuise, le blocage annoncé quand il est vide. Un cabinet qui a déclaré son
 * adresse ne voit jamais rien — c'est le cas normal.
 */
function AlerteEnvoi({ data }: { data: MessagesResult | null }) {
  if (!data || data.smtpConfigured || data.platformSendsLeft === null) return null;

  const restants = data.platformSendsLeft;

  // Quota atteint : l'alerte est dans la barre du haut et dans les réglages, pas ici.
  if (restants === 0) return null;

  if (restants <= SEUIL_ALERTE_FORFAIT) {
    return (
      <Alert tone="info" title={`${restants} envois restants`}>
        Vos messages partent pour l’instant depuis l’adresse Fidalia. Connectez celle du cabinet
        pour qu’ils partent à votre nom, sans limite.{' '}
        <Link to="/settings/sending" className="font-medium underline">
          Configurer l’envoi
        </Link>
      </Alert>
    );
  }

  return null;
}

/**
 * Les messages que le cabinet peut laisser partir, en cartes.
 *
 * Un catalogue fermé, et rien d'autre : un praticien qui ouvre cet écran le
 * premier jour n'a pas à composer quoi que ce soit — il active ce qui lui parle
 * et retouche le texte s'il le souhaite. Le catalogue et les modèles viennent
 * du serveur, qui crée les modèles par défaut du cabinet à la première lecture.
 */
function MessagesPage() {
  const { data, isPending } = useMessages();
  const { data: settings, isPending: reglagesEnCours } = useEmailSettings();
  const navigate = useNavigate();
  const { data: releveData } = useDoctolibReleve();
  const sync = useSynchroniserDoctolib();

  const modifier = useUpdateMessage();
  const creer = useCreateMessage();
  const basculerModele = useSetMessageEnabled();
  const basculerCategorieMutation = useSetCategoryEnabled();

  // La catégorie dont la mise en route est ouverte, s'il y en a une.
  const [configuration, setConfiguration] = useState<string | null>(null);
  // La catégorie qu'on voulait mettre en route quand la synchronisation a été
  // demandée à sa place. Portée à part de `configuration` : la boîte de
  // synchronisation n'est pas celle qu'on voulait ouvrir, mais on n'oublie pas
  // laquelle pour autant.
  const [syncRequisePour, setSyncRequisePour] = useState<string | null>(null);
  // Deux temps après l'activation : la confirmation, puis le test si on le
  // demande. Deux états séparés parce que l'un ouvre l'autre et qu'on revient
  // du test sur la liste, pas sur la confirmation.
  const [confirmation, setConfirmation] = useState<string | null>(null);
  const [test, setTest] = useState<MessageTemplate | null>(null);
  const [refus, setRefus] = useState<string | null>(null);

  // Mémorisés : `?? []` rend un tableau neuf à chaque rendu, et ce qui en
  // dépend — les motifs déjà pris — serait recalculé à chaque frappe ailleurs
  // dans l'écran.
  const templates = useMemo(() => data?.templates ?? [], [data]);
  const catalog = data?.catalog ?? [];
  const synchronise = releveData?.releve != null;

  /**
   * Les consignes ciblent des motifs Doctolib : sans relevé, la mise en route
   * n'aurait rien à proposer. On demande la synchronisation d'abord, plutôt
   * que d'ouvrir une boîte vide qui ne dirait pas pourquoi.
   */
  const ouvrirConfiguration = (catalogKey: string) => {
    if (INSTRUCTION_KEYS.includes(catalogKey) && !synchronise) {
      setSyncRequisePour(catalogKey);
      return;
    }
    setConfiguration(catalogKey);
  };

  /**
   * Retour sur l'onglet Fidalia après une synchronisation demandée depuis la
   * boîte : `enAttente` ne redevient `false` qu'à ce moment-là, qui est
   * exactement celui où enchaîner sur ce qu'on voulait configurer au départ.
   */
  const enAttenteAvant = useRef(sync.enAttente);
  useEffect(() => {
    if (enAttenteAvant.current && !sync.enAttente && syncRequisePour) {
      setConfiguration(syncRequisePour);
      setSyncRequisePour(null);
    }
    enAttenteAvant.current = sync.enAttente;
  }, [sync.enAttente, syncRequisePour]);

  const ouvrir = (id: string) => void navigate({ to: '/messages/$id', params: { id } });

  /** Le refus du serveur, repris tel quel : il dit ce qui manque. */
  const signaler = (error: unknown) =>
    setRefus(error instanceof Error ? error.message : 'Opération impossible.');

  /**
   * Le bouton de la carte vaut pour la catégorie entière : il éteint tout ce
   * qui part, ou rallume tout ce qu'elle contient. Un réglage ligne par ligne
   * existe juste en dessous — celui-ci répond à « est-ce que j'envoie ça ? »,
   * qui est la seule question qu'on se pose de loin.
   */
  const basculerCategorie = (catalogKey: string) => {
    const enabled = !templatesOf(templates, catalogKey).some((template) => template.enabled);
    setRefus(null);
    track('message_category_toggled', { catalog_key: catalogKey, enabled });
    basculerCategorieMutation.mutate({ catalogKey, enabled }, { onError: signaler });
  };

  const basculer = (template: MessageTemplate) => {
    setRefus(null);
    track('message_template_toggled', { enabled: !template.enabled });
    basculerModele.mutate({ id: template.id, enabled: !template.enabled }, { onError: signaler });
  };

  // Ce que les cas existants visent déjà, par catégorie : la mise en route ne
  // le proposera plus, deux textes sur un même motif partiraient tous les deux.
  const motifsPris = useMemo(
    () =>
      Object.fromEntries(
        INSTRUCTION_KEYS.map((key) => [
          key,
          casesOf(templates, key).flatMap((cas) => cas.motifIds),
        ]),
      ) as Record<string, number[]>,
    [templates],
  );

  /**
   * Les catégories qui réclament une mise en route avant de pouvoir partir.
   *
   * Ce qui leur manque n'est pas du même ordre — une donnée du cabinet pour
   * l'avis, une décision d'horaire pour le rappel, un soin à désigner pour les
   * consignes — mais la carte n'a pas à le savoir : dans tous les cas son
   * bouton ouvre une boîte au lieu d'allumer. Le serveur applique les mêmes
   * règles et refuse l'allumage sans elles (`assertActivatable`).
   */
  const aConfigurer = (catalogKey: string) => {
    if (catalogKey === GOOGLE_REVIEW) return !settings?.settings.googleReviewUrl;
    if (catalogKey === APPOINTMENT_REMINDER) {
      return fallbackOf(templates, APPOINTMENT_REMINDER)?.sendHour == null;
    }
    if (INSTRUCTION_KEYS.includes(catalogKey)) return casesOf(templates, catalogKey).length === 0;
    return false;
  };

  /**
   * Fin commune des mises en route d'une catégorie à message unique : on
   * enregistre le texte retouché, on allume avec ce que la boîte a demandé, on
   * félicite.
   *
   * Deux appels et non un : la retouche de texte et l'allumage ne sont pas le
   * même geste côté serveur — `PATCH` ne peut pas allumer, parce qu'enregistrer
   * un texte ne doit pas valoir mise en production.
   */
  const activer = async (
    template: MessageTemplate,
    champs: { subject: string; body: string },
    mise: { googleReviewUrl?: string; sendHour?: number },
  ) => {
    setRefus(null);
    try {
      await modifier.mutateAsync({ id: template.id, ...champs });
      await basculerModele.mutateAsync({ id: template.id, enabled: true, ...mise });
      setConfiguration(null);
      setConfirmation(template.catalogKey);
    } catch (error) {
      signaler(error);
    }
  };

  const entreeDe = (catalogKey: string): MessageCatalogEntry | null =>
    catalog.find((entry) => entry.key === catalogKey) ?? null;

  const avis = fallbackOf(templates, GOOGLE_REVIEW);
  const rappel = fallbackOf(templates, APPOINTMENT_REMINDER);

  if (isPending) {
    return (
      <div className="flex justify-center py-16 text-content-subtle">
        <Spinner className="size-6" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-8">
      <PageHeader
        icon={<MdStickyNote2 />}
        title="Messages"
        description="Les modèles envoyés avant et après chaque rendez-vous."
      />

      {/* Toujours affiché : le rappel vaut pour tous les ordinateurs du
          cabinet, pas seulement celui-ci — même sans message encore
          configuré. */}
      <AlerteExtensionRequise />

      {/* Rien n'est réclamé tant que le forfait offert tient : les messages
          partent par le relais Fidalia, et un praticien qui vient d'écrire son
          premier rappel n'a pas les identifiants SMTP de son hébergeur sous la
          main. L'écran ne parle d'adresse d'expédition qu'au moment où elle
          devient nécessaire — ou juste avant, pour que la fin du forfait ne
          soit pas une surprise. */}
      <AlerteEnvoi data={data ?? null} />

      {refus ? <Alert tone="danger">{refus}</Alert> : null}

      <div className="flex flex-col gap-3">
        {catalog.map((entry) => {
          const siennes = templatesOf(templates, entry.key);
          if (siennes.length === 0) return null;

          return (
            <CartePredefini
              key={entry.key}
              entry={entry}
              templates={siennes}
              aConfigurer={aConfigurer(entry.key)}
              // Le bouton de l'avis Google dépend des réglages (lien donné ou non) : tant
              // qu'ils chargent, un squelette plutôt qu'un bouton qui change en route.
              chargement={entry.key === GOOGLE_REVIEW && reglagesEnCours}
              enCoursCategorie={
                basculerCategorieMutation.isPending &&
                basculerCategorieMutation.variables?.catalogKey === entry.key
              }
              idEnCours={basculerModele.isPending ? basculerModele.variables?.id : null}
              lienInvalide={
                entry.key === GOOGLE_REVIEW &&
                Boolean(settings?.settings.googleReviewUrl) &&
                !isReviewLink(settings?.settings.googleReviewUrl ?? '')
              }
              onConfigurer={() => ouvrirConfiguration(entry.key)}
              onBasculerCategorie={() => basculerCategorie(entry.key)}
              onBasculer={(id) => {
                const template = siennes.find((candidate) => candidate.id === id);
                if (template) basculer(template);
              }}
              onOuvrir={ouvrir}
            />
          );
        })}
      </div>

      {/* Montées à l'ouverture seulement : leur remise à zéro — première
          étape, valeurs enregistrées — tient alors dans leur état initial. */}
      {avis && configuration === GOOGLE_REVIEW && entreeDe(GOOGLE_REVIEW) ? (
        <ModalAvisGoogle
          onClose={() => setConfiguration(null)}
          template={avis}
          entry={entreeDe(GOOGLE_REVIEW)!}
          url={settings?.settings.googleReviewUrl ?? ''}
          onActiver={(url, champs) => void activer(avis, champs, { googleReviewUrl: url })}
        />
      ) : null}

      {/* Les deux catégories de consignes passent par la même boîte : elle
          prend son sens — avant ou après le soin — de la catégorie qu'on lui
          confie. */}
      {INSTRUCTION_KEYS.map((catalogKey) => {
        const entry = configuration === catalogKey ? entreeDe(catalogKey) : null;
        const fallback = fallbackOf(templates, catalogKey);
        if (!entry || !fallback) return null;

        return (
          <ModalConsignes
            key={catalogKey}
            onClose={() => setConfiguration(null)}
            template={fallback}
            entry={entry}
            motifsPris={motifsPris[catalogKey]}
            onActiver={async ({ motifIds, name, subject, body }) => {
              // Un cas ciblé, et non le filet de sécurité : celui-ci partirait
              // pour tous les rendez-vous, y compris ceux qui n'ont rien de
              // chirurgical. Il reste éteint, prêt à servir si le cabinet veut
              // un texte pour le reste.
              setRefus(null);
              try {
                const { template } = await creer.mutateAsync({
                  catalogKey,
                  name,
                  subject,
                  body,
                  motifIds,
                });
                await basculerModele.mutateAsync({ id: template.id, enabled: true });
                setConfiguration(null);
                setConfirmation(catalogKey);
              } catch (error) {
                signaler(error);
              }
            }}
          />
        );
      })}

      {rappel && configuration === APPOINTMENT_REMINDER && entreeDe(APPOINTMENT_REMINDER) ? (
        <ModalRappel
          onClose={() => setConfiguration(null)}
          template={rappel}
          entry={entreeDe(APPOINTMENT_REMINDER)!}
          onActiver={(sendHour, champs) => void activer(rappel, champs, { sendHour })}
        />
      ) : null}

      <ModalMessageActif
        open={confirmation !== null}
        titre={CONFIRMATIONS[confirmation ?? '']?.titre ?? ''}
        description={CONFIRMATIONS[confirmation ?? '']?.description ?? ''}
        onTester={() => {
          // Le message de la catégorie qu'on vient d'allumer : son cas ciblé
          // s'il y en a un, son filet de sécurité sinon.
          const siennes = templatesOf(templates, confirmation ?? '');
          setTest(siennes.find((template) => template.enabled) ?? siennes[0] ?? null);
          setConfirmation(null);
        }}
        onFermer={() => setConfirmation(null)}
      />

      {test ? (
        <ModalTest open onClose={() => setTest(null)} templateId={test.id} nomModele={test.name} />
      ) : null}

      <ModalSyncDoctolib
        open={syncRequisePour !== null}
        sync={sync}
        onClose={() => setSyncRequisePour(null)}
      />
    </div>
  );
}
